import React, { useState, useEffect, useCallback, memo } from 'react';
import { Form, Input, Select, Upload, Card, Spin, message } from 'antd';
import ErrorHelper from 'helpers/ErrorHelper';
import { Button } from 'components';
import {
  UploadOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useHomepageSliders from '../useHomepageSliders';
import { Row, Col } from 'antd';
import { TranslateFormLabel } from 'components/translateInputAddon';

const { Option } = Select;
const { TextArea } = Input;

export const HomepageSliderForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);
  const {
    companies,
    shoppingCenters,
    loadingCompanies,
    createSlider,
    updateSlider,
    fetchSlider,
  } = useHomepageSliders();

  const isEditMode = !!id;

  // Function to determine available languages based on Outlet Village
  const getAvailableLanguages = () => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];

    // Check if the Outlet Village is Sicilia (you might need to adjust this condition)
    const isSicilia =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino'; // Adjust based on your data structure

    return isSicilia ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();

  useEffect(() => {
    if (isEditMode) {
      const loadSlider = async () => {
        setLoading(true);
        try {
          const slider = await fetchSlider(id);
          if (slider) {
            setInitialData(slider);

            const shoppingCenterId =
              slider.shopping_center_id ||
              slider.shopping_center?.id ||
              slider.company?.shopping_center_id ||
              slider.company_id;

            if (shoppingCenters.length > 0) {
              form.setFieldsValue({
                title: slider.title,
                title_en: slider.title_en || '',
                title_fr: slider.title_fr || '',
                description: slider.description,
                description_en: slider.description_en || '',
                description_fr: slider.description_fr || '',
                shopping_center_id: shoppingCenterId,
                position: slider.position,
              });

              // Set selected Outlet Village for language logic
              const center = shoppingCenters.find(
                (c) => (c.value || c.id) === shoppingCenterId,
              );
              setSelectedShoppingCenter(center);
            } else {
              form.setFieldsValue({
                title: slider.title,
                title_en: slider.title_en || '',
                title_fr: slider.title_fr || '',
                description: slider.description,
                description_en: slider.description_en || '',
                description_fr: slider.description_fr || '',
                position: slider.position,
              });
            }

            if (slider.file?.url) {
              setFileList([
                {
                  uid: '-1',
                  name: slider.file.file_name,
                  status: 'done',
                  url: slider.file.url,
                },
              ]);
            }
          }
        } catch (error) {
          console.error('Error loading slider:', error);
        } finally {
          setLoading(false);
        }
      };

      loadSlider();
    }
  }, [id, isEditMode, fetchSlider, form, shoppingCenters]);

  useEffect(() => {
    if (isEditMode && initialData && shoppingCenters.length > 0) {
      const shoppingCenterId =
        initialData.shopping_center_id ||
        initialData.shopping_center?.id ||
        initialData.company?.shopping_center_id ||
        initialData.company_id;

      form.setFieldsValue({
        shopping_center_id: shoppingCenterId,
      });
    }
  }, [isEditMode, initialData, shoppingCenters, form]);

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitAttempted(true);
      setLoading(true);
      try {
        if (!values.title) {
          message.error(t('Il titolo in italiano è obbligatorio'));
          setLoading(false);
          return;
        }
        const formData = {
          title: values.title,
          description: values.description || '',
          shopping_center_id: values.shopping_center_id,
          position: values.position,
          title_en: values.title_en || '',
          title_fr: values.title_fr || '',
          description_en: values.description_en || '',
          description_fr: values.description_fr || '',
        };

        if (fileList.length > 0) {
          const file = fileList[0];
          if (file.originFileObj) {
            formData.file = file.originFileObj;
          }
        }
        let result;
        if (isEditMode) {
          result = await updateSlider(id, formData);
        } else {
          result = await createSlider(formData);
        }
        if (result) {
          navigate('/website/homepage_slides');
        }
      } catch (error) {
        console.error('Error saving slider:', error);
        ErrorHelper.applyToForm(form, error, t('Failed to save slider'));
      } finally {
        setLoading(false);
      }
    },
    [isEditMode, id, fileList, createSlider, updateSlider, navigate, t, form],
  );

  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);

    if (newFileList.length > 0) {
      setSubmitAttempted(false);
    }
  }, []);

  const uploadProps = {
    fileList,
    onChange: handleFileChange,
    beforeUpload: () => false,
    accept: 'image/*,.avi,.flv,.3gp,.wmv,.mpeg,.mp4,.mov',
    maxCount: 1,
  };

  if (loading && isEditMode) {
    return (
      <Card title={t('Caricamento...')}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={isEditMode ? t('Modifica Slider') : t('Crea Nuovo Slider')}
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/website/homepage_slides')}
        >
          {t('Indietro')}
        </Button>
      }
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        scrollToFirstError
        initialValues={{
          title: '',
          title_en: '',
          title_fr: '',
          description: '',
          description_en: '',
          description_fr: '',
          shopping_center_id: undefined,
          position: undefined,
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px', fontWeight: 500, color: '#666' }}>
            {t('Contenuto')}
          </div>

          <Form.Item
          name='shopping_center_id'
          label={t('Outlet Village')}
          rules={[
            { required: true, message: t('Seleziona un Outlet Village') },
          ]}
        >
          <Select
            placeholder={t('Seleziona Outlet Village')}
            loading={loadingCompanies}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            onChange={(value) => {
              const center = shoppingCenters.find(
                (c) => (c.value || c.id) === value,
              );
              setSelectedShoppingCenter(center);
            }}
          >
            {shoppingCenters
              .filter(
                (center) =>
                  center &&
                  (center.value || center.id) &&
                  (center.title || center.name),
              )
              .map((center) => (
                <Option
                  key={center.value || center.id}
                  value={center.value || center.id}
                >
                  {center.title || center.name}
                </Option>
              ))}
          </Select>
        </Form.Item>


          {/* Titles Row */}
          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='title'
                label={
                  <TranslateFormLabel
                    label={`${t('Titolo')} IT`}
                    form={form}
                    sourceField='title'
                    textKey='title'
                    targetFields={{ en: 'title_en', fr: 'title_fr' }}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t('Il titolo in italiano è obbligatorio'),
                  },
                ]}
              >
                <Input
                  placeholder={t('Inserisci il titolo')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='title_en' label={`${t('Titolo')} EN`}>
                <Input
                  placeholder={t('Inserisci il titolo')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='title_fr' label={`${t('Titolo')} FR`}>
                  <Input
                    placeholder={t('Inserisci il titolo')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Descriptions Row */}
          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='description'
                label={
                  <TranslateFormLabel
                    label={`${t('Descrizione')} IT`}
                    form={form}
                    sourceField='description'
                    textKey='description'
                    targetFields={{
                      en: 'description_en',
                      fr: 'description_fr',
                    }}
                  />
                }
              >
                <TextArea
                  rows={4}
                  placeholder={t('Inserisci la descrizione')}
                />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='description_en'
                label={`${t('Descrizione')} EN`}
              >
                <TextArea
                  rows={4}
                  placeholder={t('Inserisci la descrizione')}
                />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item
                  name='description_fr'
                  label={`${t('Descrizione')} FR`}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('Inserisci la descrizione')}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>

    
        <Form.Item
          name='position'
          label={t('Posizione')}
          rules={[
            { required: true, message: t('La posizione è obbligatoria') },
          ]}
        >
          <Input
            type='number'
            min={1}
            placeholder={t('Inserisci la posizione')}
          />
        </Form.Item>

        <Form.Item name='file' label={t('Immagine/Video')}>
          <Upload.Dragger {...uploadProps} style={{ width: '100%' }}>
            <p className='ant-upload-drag-icon'>
              <InboxOutlined />
            </p>
            <p className='ant-upload-text'>
              {t('Click or drag file to this area to upload1')}
              'JPG', 'PNG', 'SVG', 'AVI', 'FLV', '3GP', 'WMV', 'MPEG', 'MP4',
              'MOV'
              {t('Click or drag file to this area to upload2')}
            </p>
          </Upload.Dragger>
        </Form.Item>
        {submitAttempted && fileList.length === 0 && !isEditMode && (
          <div
            style={{
              color: '#ff4d4f',
              fontSize: '14px',
              marginTop: '-20px',
              marginBottom: '10px',
            }}
          >
            {t('Non può essere lasciato in bianco')}
          </div>
        )}

        <Form.Item>
          <Button
            type='primary'
            width={'150px'}
            big
            htmlType='submit'
            loading={loading}
            icon={<SaveOutlined />}
            style={{ backgroundColor: '#002637', borderColor: '#002637' }}
          >
            {isEditMode ? t('Aggiorna') : t('Crea')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
});
